import { actions } from "@/actions";
import { NoteType } from "@/components/note/types";
import { MDSTooltip, MDSTooltipPlacement } from "@/mds/components/tooltip";
import { css } from "@/domains/emotion";
import { useAppStore } from "@/store";
import { INoteObservable } from "@/store/note/types";
import { TemplateObservable } from "@/store/templates/TemplateObservable";
import { observer } from "mobx-react-lite";
import { withLineBreaks } from "@/domains/html/withLineBreaks";
import { renderArrayWithAnds } from "@/domains/string/renderArrayWithAnds";
import { computed } from "mobx";
import { ShareSheetEntityKind, ShareSheetModal } from "@/components/modal/share-sheet";
import { EventContext } from "@/domains/metrics/context";
import { NoteObservable } from "@/store/note";
import { useTabPageStore } from "@/components/layout/components/tab-bar/use-tab-page-store";
import {
  MDSIconButtonSize,
  useTheme,
  MDSIconKind,
  MDSIconColor,
  MDSIcon,
  MDSMenuContentItem,
  MDSMenuContentItemKind,
  MDSMenuContentList,
  MDSActionMenuDropdown,
  MDSIconButton,
  MDSMenuDropdownSlotName,
} from "@/mds";

interface NoteEditorTopBarExtraActionsDropdownButtonProps {
  note?: INoteObservable | TemplateObservable;
  noteType?: NoteType;
  inSidePanel?: boolean;
}

export const NoteEditorTopBarExtraActionsDropdownButton =
  observer<NoteEditorTopBarExtraActionsDropdownButtonProps>(
    function NoteEditorTopBarExtraActionsDropdownButton({ note, noteType = NoteType.Note }) {
      const { store } = useAppStore();
      const pageStore = useTabPageStore("notesViewPage");
      const { tabId } = pageStore;
      const { theme } = useTheme();

      if (!note) return null;
      // TODO: REmove FF before rolling out
      const canShowVersions =
        note.isAvailable &&
        !note.isSuggestionActive &&
        // Require non-empty, loaded notes to allow version history
        (note instanceof NoteObservable
          ? note.isNoteContentDocumentLoaded && !note.isNoteContentInOriginalState
          : true);
      const showingVersions = pageStore.noteVersionHistory.currentNoteId == note.id;

      const menuStyles = css`
        padding: ${theme.spacing[2]} ${theme.spacing[5]};
        background: ${theme.colors.background.menu.default};
        gap: ${theme.spacing[1]};
        & > div {
          margin-left: 0;
        }
        &:hover {
          background: ${theme.colors.background.menu.hover};
        }
      `;

      const versionHistory: MDSMenuContentItem[] = canShowVersions
        ? [
            {
              id: "view-version-history",
              kind: MDSMenuContentItemKind.Item,
              label: "View version history",
              icon: MDSIconKind.History,
              isDisabled: showingVersions,
              onClick: () => pageStore.noteVersionHistory.openNoteVersionHistory(note.id),
            },
          ]
        : [];

      const sharingInfo = computed(() => {
        if (!note?.canAccess) {
          return {
            isDisabled: true,
            tooltipLabel: "You lost access",
            iconKind: () => (
              <MDSIcon kind={MDSIconKind.LockSolid} size={11} color={MDSIconColor.Secondary} />
            ),
            className: menuStyles,
          };
        }

        if (!note?.canWrite) {
          return {
            isDisabled: true,
            tooltipLabel: "You don't have write access",
            iconKind: () => (
              <MDSIcon kind={MDSIconKind.Shared} size={11} color={MDSIconColor.Secondary} />
            ),
            className: menuStyles,
          };
        }

        if (note.hasPendingShare) {
          return {
            isDisabled: false,
            tooltipLabel: "Access changes will be applied when you’re online",
            iconKind: () => (
              <MDSIcon kind={MDSIconKind.Spinner} size={11} color={MDSIconColor.Secondary} />
            ),
            className: menuStyles,
          };
        }

        if (note?.isShared) {
          const labels = note?.sharedWithLabels;
          if (labels) {
            return {
              isDisabled: false,
              tooltipLabel: withLineBreaks(
                `Shared with ${renderArrayWithAnds(
                  labels.length >= 5
                    ? [...labels.slice(0, 3), `${labels.length - 2} others`]
                    : labels
                )}`
              ),
              iconKind: () => (
                <MDSIcon kind={MDSIconKind.Shared} size={11} color={MDSIconColor.Secondary} />
              ),
              className: menuStyles,
            };
          }
        }

        return {
          isDisabled: false,
          tooltipLabel: "Private to only me",
          iconKind: () => (
            <MDSIcon kind={MDSIconKind.LockSolid} size={11} color={MDSIconColor.Secondary} />
          ),
          className: menuStyles,
        };
      }).get();

      const cleanUpButtons: MDSMenuContentItem[] =
        note instanceof NoteObservable
          ? [
              {
                id: "clean-up",
                kind: MDSMenuContentItemKind.Item,
                label: "Clean Up",
                icon: note.isProcessingSuggestion ? MDSIconKind.Spinner : MDSIconKind.Cleanup,
                onClick: () => note.cleanup(),
              },
            ]
          : [];

      const pinButtons: MDSMenuContentItem[] =
        note instanceof NoteObservable
          ? [
              {
                id: "pin",
                kind: MDSMenuContentItemKind.Item,
                label: "Pin",
                icon: note.isFavorited ? MDSIconKind.ThumbtackSolid : MDSIconKind.Thumbtack,
                onClick: () => note.toggleFavorite(),
              },
            ]
          : [];

      const contentList: MDSMenuContentList = [
        {
          id: "magic-actions",
          kind: MDSMenuContentItemKind.Header,
          label: "Magic Actions",
        },
        ...cleanUpButtons,
        {
          id: "note-actions",
          kind: MDSMenuContentItemKind.Header,
          label: "Note Actions",
        },
        ...pinButtons,
        {
          id: "find-in-note",
          kind: MDSMenuContentItemKind.Item,
          label: "Find in note",
          icon: MDSIconKind.Find,
          onClick: () => pageStore.findInNote.open(),
        },
        {
          id: "share",
          kind: MDSMenuContentItemKind.Item,
          label: "Share",
          isDisabled: sharingInfo.isDisabled,
          icon: sharingInfo.iconKind,
          onClick: () =>
            pageStore.shareSheetModal.open({
              id: note.id,
              entityKind: ShareSheetEntityKind.Note,
              eventContext: EventContext.EditorActions,
            }),
        },
        {
          id: "copy-note-link",
          kind: MDSMenuContentItemKind.Item,
          label: "Copy link",
          icon: MDSIconKind.Link,
          onClick: () => actions.copyNoteLinkToClipboard({ noteId: note.id, noteType }),
        },
        ...versionHistory,
        {
          id: "move-to-trash",
          kind: MDSMenuContentItemKind.Item,
          label: "Trash",
          icon: MDSIconKind.Trash,
          onClick: async () => {
            if (note instanceof TemplateObservable) {
              await note.delete();
            } else {
              await note.moveToTrash();
            }
            switch (noteType) {
              case NoteType.Note:
                store.navigation.tabs.closeTab({ tabId });
                break;
              case NoteType.Template:
                store.navigation.goToTemplates();
                break;
            }
          },
        },
      ];

      return (
        <>
          <ShareSheetModal store={pageStore.shareSheetModal} />
          <MDSTooltip config={{ label: "More", placement: MDSTooltipPlacement.Top }}>
            <MDSActionMenuDropdown contentList={contentList} placement="bottom right">
              <MDSIconButton
                ref={pageStore.findInNote.anchorRef}
                slot={MDSMenuDropdownSlotName.Trigger}
                iconKind={MDSIconKind.More}
                size={MDSIconButtonSize.Medium}
              />
            </MDSActionMenuDropdown>
          </MDSTooltip>
        </>
      );
    }
  );
